// Standalone test harness for the import-installations-ui widget.
//
// In production the widget is mounted by a host app (service-portal)
// that hands it a bootstrap token via the `bootstrapToken` JS property.
// This harness simulates that flow for standalone testing:
//
//   1. GET /auth/bootstrap-token on the BFF. Returns access token from
//      session cookie. On 401 we redirect to GET /auth/login?return_to=…;
//      the BFF runs the auth-code flow against Keycloak (confidential
//      client, secret held server-side), seals the upstream token into
//      the session cookie, and redirects the browser back here.
//   2. Create <import-installations-ui>, assign the fetched token to its
//      bootstrapToken JS property, attach to the page — the widget's
//      connectedCallback then performs the BFF /auth/exchange handshake
//      exactly as it does under a real host app.
//   3. On mfe-token-refresh-needed the harness re-fetches the bootstrap
//      token from the BFF and pushes the new value into the widget.

import "./main.ts";
import { API_HOST } from "$lib/environment";

const AUDIENCE = "import-installations-api";

interface BootstrapTokenResponse {
  access_token: string;
}

const redirectToLogin = (): void => {
  const returnTo = encodeURIComponent(window.location.href);
  window.location.assign(`${API_HOST}/auth/login?return_to=${returnTo}`);
};

async function fetchBootstrapToken(): Promise<string | null> {
  const res = await fetch(`${API_HOST}/auth/bootstrap-token`, {
    credentials: "include",
  });
  if (res.status === 401) {
    return null;
  }
  if (!res.ok) {
    throw new Error(`/auth/bootstrap-token returned ${res.status}`);
  }
  const { access_token } = (await res.json()) as BootstrapTokenResponse;
  return access_token;
}

async function bootstrap(): Promise<void> {
  const token = await fetchBootstrapToken();
  if (!token) {
    redirectToLogin();
    return;
  }

  const element = document.createElement(
    "import-installations-ui",
  ) as HTMLElement & {
    bootstrapToken?: string;
    audience?: string;
  };
  element.setAttribute("data-originating-env", "www1-test");
  element.audience = AUDIENCE;
  element.bootstrapToken = token;

  element.addEventListener("mfe-token-refresh-needed", () => {
    fetchBootstrapToken()
      .then((next) => {
        if (next) {
          element.bootstrapToken = next;
        } else {
          redirectToLogin();
        }
      })
      .catch((err) => {
        console.error("Test harness: bootstrap-token refresh failed", err);
      });
  });

  document.body.appendChild(element);
}

bootstrap().catch((err) => {
  console.error("Test harness bootstrap failed", err);
});
